import {
  Autocomplete,
  Dropdown,
  FormikDatePicker,
  MoneyField,
  PageSpinner,
  Section,
  useNotifications,
} from '@armhr/ui';
import LockIcon from '@mui/icons-material/Lock';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  TextField as MuiTextField,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { useCompany } from '../../contexts/company.context';
import { useApi } from '../../hooks/useApi';
import { useApiData } from '../../hooks/useApiData';
import {
  ethnicities,
  gender as genderOptions,
  languages,
  maritalStatuses,
  payMethods,
  payRates,
} from '../../utils/constants';
import { paths } from '../../utils/paths';

const validationSchema = Yup.object().shape({
  status: Yup.string().required('Rehired employment status is required'),
  rehire_date: Yup.string().required('Rehire date is required'),
  type: Yup.string().required('Employment type is required'),
  reason: Yup.string().required('Rehire reason is required'),
  location: Yup.string().required('Work location is required'),
  position: Yup.string().required('Position is required'),
  benefit_group: Yup.string().required('Benefit group is required'),
  pay_group: Yup.string().required('Pay group is required'),
  pay_method: Yup.string().required('Pay method is required'),
  standard_hours: Yup.string().required('Standard hours is required'),
  personal_email: Yup.string()
    .email('Enter a valid email')
    .when('work_email', {
      is: (work_email: string) => !work_email,
      then: (schema) => schema.required('A personal or work email is required'),
    }),
  work_email: Yup.string().email('Enter a valid email'),
});

const lockAdornment = (
  <InputAdornment position="end">
    <LockIcon fontSize="small" color="disabled" />
  </InputAdornment>
);

const RehireSetupPage = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const api = useApi();
  const company = useCompany();
  const { showNotification } = useNotifications();

  const { data: detail, loading: detailLoading } =
    useApiData<TerminatedEmployeeDetail>((api) =>
      api.onboarding.getTerminatedEmployee(employeeId!)
    );

  const { data: codes, loading: codesLoading } = useApiData<CompanyCode>(
    (api) => api.company.getCodes()
  );

  const { data: employees, loading: employeesLoading } = useApiData<
    PublicEmployeeProfile[]
  >((api) => api.company.getEmployees());

  const managers = useMemo(() => {
    return employees?.map((e) => ({
      employee_id: e.id,
      first_name: e.first_name || '',
      last_name: e.last_name || '',
    })) as PrismSecurityUser[] | null;
  }, [employees]);

  if (detailLoading || codesLoading || employeesLoading || !detail)
    return <PageSpinner />;

  const initialValues: RehireFormValues = {
    employee_id: detail.employee_id,
    first_name: detail.first_name || '',
    last_name: detail.last_name || '',
    rehire_date: '',
    status: 'A',
    type: detail.employee_type || '',
    reason: '',
    stop_previous_voluntary_deductions: false,
    stop_previous_direct_deposit: false,
    auto_enroll_benefits: false,
    seniority_date: '',
    location: detail.location || '',
    position: detail.position || '',
    benefit_group: detail.benefit_group || '',
    retirement_benefit_group: detail.retirement_benefit_group || '',
    pay_group: detail.pay_group || '',
    pay_method: detail.pay_method || '',
    standard_hours: detail.standard_hours || '',
    personal_email: detail.personal_email || '',
    work_email: detail.work_email || '',
    mobile_phone: detail.mobile_phone || '',
    division: detail.division || '',
    department: detail.department || '',
    shift: detail.shift || '',
    project: detail.project || '',
    work_group: detail.work_group || '',
    union_code: detail.union_code || '',
    reports_to: detail.reports_to || '',
    manager: detail.manager || '',
    pay_rate: detail.pay_rate || '',
    pay_rate_basis: detail.pay_rate_basis || '',
    ignore_okay_to_rehire_flag: false,
  };

  const doNotRehire = (detail.rehire_ok || '').toUpperCase() === 'N';

  const handleSubmit = async (values: RehireFormValues) => {
    try {
      await api.onboarding.rehireEmployee(values);

      navigate(paths.newHires, { state: { persistNotification: true } });
      showNotification({
        message: (
          <>
            Rehire submitted successfully for{' '}
            <strong>
              {detail.first_name} {detail.last_name}
            </strong>
            . Onboarding has started automatically.
          </>
        ),
        severity: 'success',
      });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      const errorDetail = error?.response?.data?.detail;

      showNotification({
        message: (
          <>
            There was a problem processing the rehire.
            {errorDetail && (
              <Box>
                <Typography variant="body2" color="error.light">
                  {errorDetail}
                </Typography>
              </Box>
            )}
          </>
        ),
        severity: 'error',
      });
      window?.scrollTo(0, 0);
    }
  };

  return (
    <>
      <Helmet>
        <title>Rehire Setup | Armhr</title>
      </Helmet>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange
        validateOnBlur
      >
        {({ isSubmitting, values, setFieldValue, handleChange }) => (
          <Form noValidate>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}
            >
              <Box>
                <Typography variant="h2" mb={0.5}>
                  Rehire setup for {company?.name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  On submit, the new hire will be send an onboarding email.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={() => window.history.back()}
                >
                  Back
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  disabled={doNotRehire && !values.ignore_okay_to_rehire_flag}
                >
                  Save rehire
                </LoadingButton>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              Personal information is pre-filled from the employee&apos;s
              previous record. Locked fields cannot be changed during rehire.
            </Alert>

            <Alert severity="warning" sx={{ mb: doNotRehire ? 2 : 4 }}>
              Changes in the Employment Details and Pay Details sections will be
              immediate. Future dated changes are not supported.
            </Alert>

            {doNotRehire && (
              <Alert severity="error" sx={{ mb: 4 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  This employee is marked Do Not Rehire in PrismHR.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Proceeding will override the Do Not Rehire flag. Only continue
                  if you have explicit authorization.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.ignore_okay_to_rehire_flag}
                      onChange={(e) =>
                        setFieldValue(
                          'ignore_okay_to_rehire_flag',
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="I acknowledge this employee is marked Do Not Rehire and I am authorized to override it."
                />
              </Alert>
            )}

            {/* Personal information */}
            <Section
              title="Personal information"
              description="Pre-filled from record"
            >
              <Stack spacing={3}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <MuiTextField
                    label="Legal first name"
                    value={detail.first_name}
                    fullWidth
                    InputProps={{ readOnly: true, endAdornment: lockAdornment }}
                    required
                  />
                  <MuiTextField
                    label="Middle initial"
                    value={detail.middle_initial || ''}
                    fullWidth
                    InputProps={{ readOnly: true, endAdornment: lockAdornment }}
                  />
                  <MuiTextField
                    label="Legal last name"
                    value={detail.last_name}
                    fullWidth
                    InputProps={{ readOnly: true, endAdornment: lockAdornment }}
                    required
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <MuiTextField
                    label="Birth date"
                    value={detail.birth_date || ''}
                    fullWidth
                    InputProps={{ readOnly: true, endAdornment: lockAdornment }}
                    required
                  />
                  <MuiTextField
                    label="SSN"
                    value={
                      detail.ssn_last_four
                        ? `***-**-${detail.ssn_last_four}`
                        : ''
                    }
                    fullWidth
                    InputProps={{ readOnly: true, endAdornment: lockAdornment }}
                  />
                  <Dropdown
                    name="gender_display"
                    label="Gender"
                    value={detail.gender || ''}
                    fullWidth
                    disabled
                    options={genderOptions.map((g) => ({
                      value: g.value,
                      label: g.label,
                    }))}
                    onChange={() => {}}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Field
                    component={TextField}
                    name="personal_email"
                    label="Personal email"
                    fullWidth
                    required
                  />
                  <Field
                    component={TextField}
                    name="work_email"
                    label="Work email"
                    fullWidth
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <MuiTextField
                    label="Preferred name"
                    value={detail.preferred_name || ''}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <Dropdown
                    name="marital_status_display"
                    label="Marital status"
                    value={detail.marital_status || ''}
                    fullWidth
                    disabled
                    options={maritalStatuses.map((s) => ({
                      value: s.value,
                      label: s.label,
                    }))}
                    onChange={() => {}}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Dropdown
                    name="ethnicity_display"
                    label="Ethnicity"
                    value={detail.ethnicity || ''}
                    fullWidth
                    disabled
                    options={ethnicities.map((e) => ({
                      value: e.value,
                      label: e.label,
                    }))}
                    onChange={() => {}}
                  />
                  <Dropdown
                    name="language_display"
                    label="Preferred Language"
                    value={detail.preferred_language || ''}
                    fullWidth
                    disabled
                    options={languages.map((l) => ({
                      value: l.value,
                      label: l.label,
                    }))}
                    onChange={() => {}}
                  />
                </Box>
              </Stack>
            </Section>

            <Divider sx={{ mt: 3, mb: 6 }} />

            {/* Rehire settings */}
            <Section
              title="Rehire settings"
              description={
                <Typography variant="body2" color="primary">
                  Configure rehire options
                </Typography>
              }
            >
              <Stack spacing={3}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Dropdown
                    name="status"
                    label="Rehired employment status"
                    fullWidth
                    required
                    value={values.status}
                    options={
                      codes?.status_codes?.map((c) => ({
                        value: c.id,
                        label: c.value,
                      })) || []
                    }
                    onChange={handleChange}
                  />
                  <Dropdown
                    name="type"
                    label="Rehired employment type"
                    fullWidth
                    required
                    value={values.type}
                    options={
                      codes?.type_codes?.map((c) => ({
                        value: c.id,
                        label: c.value,
                      })) || []
                    }
                    onChange={handleChange}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormikDatePicker
                    name="rehire_date"
                    label="Rehire date"
                    required
                  />
                  <Dropdown
                    name="reason"
                    label="Reason code"
                    fullWidth
                    required
                    value={values.reason}
                    options={
                      codes?.reason_codes?.map((c) => ({
                        value: c.id,
                        label: c.value,
                      })) || []
                    }
                    onChange={handleChange}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl>
                    <FormLabel>Voluntary deductions</FormLabel>
                    <RadioGroup
                      row
                      value={
                        values.stop_previous_voluntary_deductions
                          ? 'stop'
                          : 'continue'
                      }
                      onChange={(e) =>
                        setFieldValue(
                          'stop_previous_voluntary_deductions',
                          e.target.value === 'stop'
                        )
                      }
                    >
                      <FormControlLabel
                        value="stop"
                        control={<Radio />}
                        label="Stop previous deductions"
                      />
                      <FormControlLabel
                        value="continue"
                        control={<Radio />}
                        label="Continue previous deductions"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl>
                    <FormLabel>Direct deposit</FormLabel>
                    <RadioGroup
                      row
                      value={
                        values.stop_previous_direct_deposit
                          ? 'stop'
                          : 'continue'
                      }
                      onChange={(e) =>
                        setFieldValue(
                          'stop_previous_direct_deposit',
                          e.target.value === 'stop'
                        )
                      }
                    >
                      <FormControlLabel
                        value="stop"
                        control={<Radio />}
                        label="Stop previous direct deposit"
                      />
                      <FormControlLabel
                        value="continue"
                        control={<Radio />}
                        label="Continue previous direct deposit"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl>
                    <FormLabel>Benefit (Medical, Retirement, PTO)</FormLabel>
                    <RadioGroup
                      row
                      value={values.auto_enroll_benefits ? 'auto' : 'none'}
                      onChange={(e) =>
                        setFieldValue(
                          'auto_enroll_benefits',
                          e.target.value === 'auto'
                        )
                      }
                    >
                      <FormControlLabel
                        value="none"
                        control={<Radio />}
                        label="Do not auto enroll"
                      />
                      <FormControlLabel
                        value="auto"
                        control={<Radio />}
                        label="Auto enroll"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormikDatePicker
                    name="seniority_date"
                    label="Seniority date"
                  />
                </Box>
              </Stack>
            </Section>

            <Divider sx={{ mt: 3, mb: 6 }} />

            {/* Employment details */}
            <Section
              title="Employment details"
              description={
                <Typography variant="body2" color="primary">
                  Update for rehire
                </Typography>
              }
            >
              <Stack spacing={3}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Autocomplete
                    name="location"
                    label="Work location"
                    fullWidth
                    required
                    value={values.location}
                    options={
                      codes?.location_codes?.map((c) => ({
                        value: c.id,
                        label: c.value,
                      })) || []
                    }
                    onChange={(val: string) => setFieldValue('location', val)}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Autocomplete
                    name="position"
                    label="Position"
                    fullWidth
                    required
                    value={values.position}
                    options={
                      codes?.job_codes?.map((c) => ({
                        value: c.id,
                        label: c.value,
                      })) || []
                    }
                    onChange={(val: string) => setFieldValue('position', val)}
                  />
                  <Autocomplete
                    name="department"
                    label="Department"
                    fullWidth
                    value={values.department}
                    options={
                      codes?.department_codes?.map((c) => ({
                        value: c.id,
                        label: c.value,
                      })) || []
                    }
                    onChange={(val: string) => setFieldValue('department', val)}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Dropdown
                    name="benefit_group"
                    label="Benefit group"
                    fullWidth
                    required
                    value={values.benefit_group}
                    options={
                      codes?.benefit_group_codes?.map((c) => ({
                        value: c.id,
                        label: c.value,
                      })) || []
                    }
                    onChange={handleChange}
                  />
                  <Dropdown
                    name="retirement_benefit_group"
                    label="Retirement benefit group"
                    fullWidth
                    value={values.retirement_benefit_group}
                    options={
                      codes?.benefit_group_codes?.map((c) => ({
                        value: c.id,
                        label: c.value,
                      })) || []
                    }
                    onChange={handleChange}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Autocomplete
                    name="division"
                    label="Division"
                    fullWidth
                    value={values.division}
                    options={
                      codes?.division_codes?.map((c) => ({
                        value: c.id,
                        label: c.value,
                      })) || []
                    }
                    onChange={(val: string) => setFieldValue('division', val)}
                  />
                  <Autocomplete
                    name="shift"
                    label="Shift"
                    fullWidth
                    value={values.shift}
                    options={
                      codes?.shift_codes?.map((c) => ({
                        value: c.id,
                        label: c.value,
                      })) || []
                    }
                    onChange={(val: string) => setFieldValue('shift', val)}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Autocomplete
                    name="reports_to"
                    label="PTO Approver"
                    fullWidth
                    value={values.reports_to}
                    options={
                      managers?.map((m) => ({
                        value: m.employee_id,
                        label: `${m.first_name} ${m.last_name}`,
                      })) || []
                    }
                    onChange={(val: string) => setFieldValue('reports_to', val)}
                  />
                  <Autocomplete
                    name="manager"
                    label="Manager"
                    fullWidth
                    value={values.manager}
                    options={
                      managers?.map((m) => ({
                        value: m.employee_id,
                        label: `${m.first_name} ${m.last_name}`,
                      })) || []
                    }
                    onChange={(val: string) => setFieldValue('manager', val)}
                  />
                </Box>
              </Stack>
            </Section>

            <Divider sx={{ mt: 3, mb: 6 }} />

            {/* Pay details */}
            <Section title="Pay details">
              <Stack spacing={3}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Dropdown
                    name="pay_group"
                    label="Pay group"
                    fullWidth
                    required
                    value={values.pay_group}
                    options={
                      codes?.pay_group_codes?.map((c) => ({
                        value: c.id,
                        label: c.value,
                      })) || []
                    }
                    onChange={handleChange}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Dropdown
                    name="pay_method"
                    label="Pay method"
                    fullWidth
                    required
                    value={values.pay_method}
                    options={payMethods.map((m) => ({
                      value: m.id,
                      label: m.value,
                    }))}
                    onChange={handleChange}
                  />
                  <Field
                    component={TextField}
                    name="standard_hours"
                    label="Standard hours"
                    fullWidth
                    required
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <MoneyField
                    name="pay_rate"
                    label="Pay rate"
                    fullWidth
                    onChange={handleChange}
                  />
                  <Dropdown
                    name="pay_rate_basis"
                    label="Pay rate basis"
                    fullWidth
                    value={values.pay_rate_basis}
                    options={payRates.map((r) => ({
                      value: r.id,
                      label: r.value,
                    }))}
                    onChange={handleChange}
                  />
                </Box>
              </Stack>
            </Section>

            <Divider sx={{ mt: 3, mb: 4 }} />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mb: 8,
              }}
            >
              <Button
                color="secondary"
                variant="outlined"
                onClick={() => window.history.back()}
              >
                Back
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isSubmitting}
                disabled={doNotRehire && !values.ignore_okay_to_rehire_flag}
              >
                Save rehire
              </LoadingButton>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default RehireSetupPage;
